import { useEffect, useMemo, useRef } from 'react';
import { BrowserRouter as Router } from 'react-router-dom';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import { createAppTheme } from './theme';
import AppRoutes from './routes';
import BackButtonHandler from './components/common/BackButtonHandler';
import ScrollToTop from './components/common/ScrollToTop';
import PresenceManager from './components/common/PresenceManager';
import OfflineBanner from './components/common/OfflineBanner';
import NotificationStateSync from './components/common/NotificationStateSync';
import IdleTimeoutManager from './components/common/IdleTimeoutManager';
import './styles/index.css';
import { onIdTokenChanged, signOut, type User } from 'firebase/auth';
import { auth } from './firebase';
import { jwtDecode } from 'jwt-decode';
import { StatusBar, Style } from '@capacitor/status-bar';
import { Capacitor } from '@capacitor/core';
import { getSensitiveData, saveSensitiveData, removeSensitiveData } from './utils/secureStorage';
import { useAppDispatch, useAppSelector } from './store';
import { fetchAppConfig } from './store/reducers/appConfigSlice';
import { fetchManagerCenterAccess } from './store/reducers/managerCenterAccessSlice';
import { finishAuthInitialization, resetAuth, restoreSession } from './store/reducers/authSlice';
import { fetchProfileSummary, setAuthToken, setEmail, setUserId, updatePersonalInfo } from './redux/slices/userProfileSlice';
import { ThemeProvider, useTheme } from './context/ThemeContext';
import { ensureAccountClaims } from './services/api/accountClaimsGate';
import { IDLE_LOGOUT_REASON_KEY } from './components/common/IdleTimeoutManager';

interface StoredUserSummary {
  email?: string;
  name?: string;
  profileImage?: string | null;
}

const getNameParts = (name: string) => {
  const [firstName = '', ...lastNameParts] = name.trim().split(/\s+/).filter(Boolean);
  return { firstName, lastName: lastNameParts.join(' ') };
};

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

// Retries transient failures (network blips, etc.) instead of dropping a valid
// Firebase session and stranding the user in a logged-out state on reload.
const RESTORE_SESSION_MAX_RETRIES = 2;
const RESTORE_SESSION_RETRY_DELAY_MS = 1000;

function ThemedApplication() {
  const { theme } = useTheme();
  const muiTheme = useMemo(() => createAppTheme(theme), [theme]);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    const setStatusBarAppearance = async () => {
      try {
        await StatusBar.setOverlaysWebView({ overlay: false });
        await StatusBar.setStyle({ style: theme.isDark ? Style.Light : Style.Dark });
      } catch (error) {
        console.error('Failed to set status bar style:', error);
      }
    };

    setStatusBarAppearance();
  }, [theme.isDark]);

  return (
    <MuiThemeProvider theme={muiTheme}>
      <CssBaseline />
      <Router>
        <ScrollToTop />
        <OfflineBanner />
        <PresenceManager />
        <NotificationStateSync />
        <IdleTimeoutManager />
        <BackButtonHandler />
        <div className="app-container">
          <AppRoutes />
        </div>
      </Router>
    </MuiThemeProvider>
  );
}

function App() {
  const dispatch = useAppDispatch();
  const isAuthenticated = useAppSelector((s) => s.auth.isAuthenticated);
  const configLoaded = useAppSelector((s) => s.appConfig.isLoaded);
  const managerAccessChecked = useAppSelector((s) => s.managerCenterAccess.isChecked);
  const profileSummaryLoaded = useAppSelector((s) => s.userProfile.isProfileSummaryLoaded);

  useEffect(() => {
    if (isAuthenticated && !configLoaded) {
      dispatch(fetchAppConfig());
    }
  }, [configLoaded, dispatch, isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated && !managerAccessChecked) {
      dispatch(fetchManagerCenterAccess());
    }
  }, [dispatch, isAuthenticated, managerAccessChecked]);

  useEffect(() => {
    if (isAuthenticated && !profileSummaryLoaded) {
      dispatch(fetchProfileSummary());
    }
  }, [dispatch, isAuthenticated, profileSummaryLoaded]);

  const lastVerifiedRef = useRef<{ uid: string; at: number } | null>(null);
  const CLAIMS_RECHECK_COOLDOWN_MS = 15_000;

  useEffect(() => {
    const restoreFromUser = async (user: User) => {
      if (!user.emailVerified) {
        await signOut(auth);
        return;
      }

      const recentlyVerified =
        lastVerifiedRef.current?.uid === user.uid &&
        Date.now() - lastVerifiedRef.current.at < CLAIMS_RECHECK_COOLDOWN_MS;

      let freshToken: string;

      if (recentlyVerified) {
        freshToken = await user.getIdToken();
      } else {
        const claimsResult = await ensureAccountClaims(user);

        if (!claimsResult.ok) {
          await signOut(auth);
          try {
            sessionStorage.setItem(IDLE_LOGOUT_REASON_KEY, `disabled:${claimsResult.message}`);
          } catch {
            // ignore
          }
          return;
        }

        freshToken = claimsResult.token;
        lastVerifiedRef.current = { uid: user.uid, at: Date.now() };
      }

      const { userId } = jwtDecode<{ userId?: string }>(freshToken);

      if (!userId) {
        throw new Error('User ID claim is missing from the authentication token');
      }

      await saveSensitiveData('authToken', freshToken);
      await saveSensitiveData('userId', userId);
      dispatch(restoreSession({
        token: freshToken,
        userId,
        email: user.email,
      }));
      dispatch(setUserId(userId));
      dispatch(setAuthToken(freshToken));
      dispatch(setEmail(user.email ?? ''));

      const storedUserData = await getSensitiveData('userData');
      if (storedUserData) {
        try {
          const userSummary: StoredUserSummary = JSON.parse(storedUserData);
          const isCurrentUser = !userSummary.email || userSummary.email.toLowerCase() === user.email?.toLowerCase();

          if (isCurrentUser && userSummary.name) {
            const { firstName, lastName } = getNameParts(userSummary.name);
            dispatch(updatePersonalInfo({
              firstName,
              lastName,
              profileImage: userSummary.profileImage || undefined,
            }));
          }
        } catch {
          // Ignore malformed cached profile data; the dashboard will refresh it from the API.
        }
      }
    };

    const unsubscribe = onIdTokenChanged(auth, async (user) => {
      if (user) {
        for (let attempt = 0; attempt <= RESTORE_SESSION_MAX_RETRIES; attempt++) {
          try {
            await restoreFromUser(user);
            return;
          } catch (err) {
            if (attempt === RESTORE_SESSION_MAX_RETRIES) {
              // Retries exhausted — surface as loading-complete without dropping
              // any previously restored session, so a transient failure doesn't
              // strand an otherwise-valid Firebase session as logged-out.
              console.error('Failed to restore session after retries:', err);
              dispatch(finishAuthInitialization());
              return;
            }
            await sleep(RESTORE_SESSION_RETRY_DELAY_MS * 2 ** attempt);
          }
        }
      } else {
        await removeSensitiveData('authToken');
        await removeSensitiveData('userId');
        dispatch(resetAuth());
      }
    });

    return () => unsubscribe();
  }, [dispatch]);

  return (
    <ThemeProvider>
      <ThemedApplication />
    </ThemeProvider>
  );
}

export default App;
